import { useState } from 'react';
import { cities } from '../data/citiesData';
import { formatILS, formatPercent, calcGrossYield, calcAirbnbRevenue } from '../utils/format';

function ClassicSimulator() {
  const [cityId, setCityId] = useState('');
  const [price, setPrice] = useState('');
  const [rent, setRent] = useState('');
  const [result, setResult] = useState(null);

  const handleCityChange = (e) => {
    setCityId(e.target.value);
    const city = cities.find(c => c.id === e.target.value);
    if (city) {
      setPrice(city.rentalYields[1].averagePrice);
      setRent(city.rentalYields[1].averageMonthlyRent);
    }
  };

  const calculate = () => {
    const p = parseFloat(price), r = parseFloat(rent);
    if (!p || !r) return;
    const annual = r * 12;
    const yld = calcGrossYield(r, p);
    setResult({ annual, yld });
  };

  const cardStyle = {
    background: 'var(--color-bg-white)',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    border: '1px solid var(--color-border)',
  };

  return (
    <div style={cardStyle}>
      <h3 className="card-title">Location classique</h3>
      <p style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>Estimez votre rendement locatif annuel brut.</p>
      <div className="form-group">
        <label className="form-label">Ville (optionnel — charge les valeurs par défaut)</label>
        <select className="form-select" value={cityId} onChange={handleCityChange}>
          <option value="">-- Choisir une ville --</option>
          {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Prix d'achat (₪)</label>
        <input className="form-input" type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="Ex : 1 500 000" />
      </div>
      <div className="form-group">
        <label className="form-label">Loyer mensuel (₪)</label>
        <input className="form-input" type="number" value={rent} onChange={e => setRent(e.target.value)} placeholder="Ex : 5 000" />
      </div>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button className="btn btn-primary" onClick={calculate}>Calculer</button>
        <button className="btn btn-outline" onClick={() => { setPrice(''); setRent(''); setCityId(''); setResult(null); }}>Réinitialiser</button>
      </div>
      {result && (
        <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="result-box">
            <span className="result-value">{formatILS(result.annual, true)}</span>
            <span className="result-label">Revenu annuel brut</span>
          </div>
          <div className="result-box" style={{ background: '#0f3320' }}>
            <span className="result-value">{formatPercent(result.yld)}</span>
            <span className="result-label">Rendement brut</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AirbnbSimulator() {
  const [cityId, setCityId] = useState('');
  const [price, setPrice] = useState('');
  const [daily, setDaily] = useState('');
  const [occ, setOcc] = useState('');
  const [result, setResult] = useState(null);

  const handleCityChange = (e) => {
    setCityId(e.target.value);
    const city = cities.find(c => c.id === e.target.value);
    if (city) {
      setPrice(city.rentalYields[0].averagePrice);
      setDaily(city.airbnb.averageDailyRate);
      setOcc(city.airbnb.averageOccupancyRate);
    }
  };

  const calculate = () => {
    const p = parseFloat(price), d = parseFloat(daily), o = parseFloat(occ);
    if (!p || !d || !o) return;
    const annual = calcAirbnbRevenue(d, o);
    const yld = (annual / p) * 100;
    setResult({ annual, yld });
  };

  const cardStyle = {
    background: 'var(--color-bg-white)',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    border: '1px solid var(--color-border)',
  };

  return (
    <div style={cardStyle}>
      <h3 className="card-title">Location courte durée</h3>
      <p style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>Estimez votre rendement en location saisonnière.</p>
      <div className="form-group">
        <label className="form-label">Ville (charge les valeurs par défaut)</label>
        <select className="form-select" value={cityId} onChange={handleCityChange}>
          <option value="">-- Choisir une ville --</option>
          {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {cityId && <p className="form-hint">Valeurs par défaut chargées pour {cities.find(c => c.id === cityId)?.name}</p>}
      </div>
      <div className="form-group">
        <label className="form-label">Prix d'achat (₪)</label>
        <input className="form-input" type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="Ex : 1 000 000" />
      </div>
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Tarif journalier (₪)</label>
          <input className="form-input" type="number" value={daily} onChange={e => setDaily(e.target.value)} placeholder="Ex : 400" />
        </div>
        <div className="form-group">
          <label className="form-label">Taux d'occupation (%)</label>
          <input className="form-input" type="number" min="0" max="100" value={occ} onChange={e => setOcc(e.target.value)} placeholder="Ex : 65" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button className="btn btn-primary" onClick={calculate}>Calculer</button>
        <button className="btn btn-outline" onClick={() => { setPrice(''); setDaily(''); setOcc(''); setCityId(''); setResult(null); }}>Réinitialiser</button>
      </div>
      {result && (
        <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="result-box">
            <span className="result-value">{formatILS(result.annual, true)}</span>
            <span className="result-label">Revenu annuel brut estimé</span>
          </div>
          <div className="result-box" style={{ background: '#3d1a00' }}>
            <span className="result-value">{formatPercent(result.yld)}</span>
            <span className="result-label">Rendement brut</span>
          </div>
        </div>
      )}
      <div className="insight-box" style={{ marginTop: '1.25rem' }}>
        Formule : Tarif journalier × (Taux d'occupation / 100) × 365 = Revenu annuel brut
      </div>
    </div>
  );
}

export default function Simulators() {
  return (
    <div className="page-content">
      <div className="container">
        <span className="gold-tag">Outils de calcul</span>
        <h1>Simulateurs de rendement</h1>
        <p style={{ marginBottom: '2.5rem', fontSize: '1.05rem' }}>
          Estimez votre rendement locatif brut en quelques secondes. Les valeurs par défaut sont basées sur nos données de marché.
        </p>
        <div className="grid-2">
          <ClassicSimulator />
          <AirbnbSimulator />
        </div>
        <div className="insight-box" style={{ marginTop: '2rem' }}>
          Ces simulateurs calculent des <strong>rendements bruts</strong>. Le rendement net (après charges de copropriété, taxes, frais de gestion, assurances, impôts) est généralement de 20–35 % inférieur. Consultez un professionnel pour une simulation complète.
        </div>
      </div>
    </div>
  );
}
